/**
 * Payment Packages API client
 *
 * Phase 4-1 / Stream 4 / Plan §12 / Design §4.4 C1 付费档位管理.
 *
 * 包装 axios apiClient 调用 admin-service /packages 端点；返回类型与
 * admin-service ServiceResponse 形状对齐。所有写动作通过 client.ts 自动注入
 * Idempotency-Key（拦截器层处理，与 admins.ts 一致）。
 *
 * 业务规则错误（如 active 状态调价被拒、版本冲突）由后端返回 51201；
 * 前端 onError 中读取 message 直接展示给用户即可。
 */
import { apiClient } from './client';

export type PackageStatus = 'active' | 'archived';

export interface PackageDto {
  id: string;
  name: string;
  description: string | null;
  priceCents: number;
  bookCount: number;
  tag: string | null;
  status: PackageStatus;
  displayOrder: number;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  archivedAt: string | null;
  archivedBy: string | null;
}

export interface ListPackagesFilter {
  status?: PackageStatus;
}

export interface ListPackagesResponse {
  items: PackageDto[];
  total: number;
}

export interface CreatePackageInput {
  id: string;
  name: string;
  description?: string | null;
  priceCents: number;
  bookCount: number;
  tag?: string | null;
  displayOrder?: number;
}

export interface UpdatePackageInput {
  name?: string;
  description?: string | null;
  priceCents?: number;
  bookCount?: number;
  tag?: string | null;
  displayOrder?: number;
  /** 乐观锁；与 DB 不匹配返回 51201 + "版本冲突" */
  version: number;
}

interface ServiceResponse<T> {
  code: number;
  message: string;
  data: T;
  requestId: string;
}

/** GET /packages — 列表（可选 status 过滤）。 */
export async function listPackages(
  filter: ListPackagesFilter = {},
): Promise<ListPackagesResponse> {
  const params: Record<string, string> = {};
  if (filter.status) params.status = filter.status;
  const resp = await apiClient.get<ServiceResponse<ListPackagesResponse>>('/packages', {
    params,
  });
  return resp.data.data;
}

/** GET /packages/:id — 详情。 */
export async function getPackage(id: string): Promise<PackageDto> {
  const resp = await apiClient.get<ServiceResponse<PackageDto>>(
    `/packages/${encodeURIComponent(id)}`,
  );
  return resp.data.data;
}

/** POST /packages — 创建（默认 status='archived'）。 */
export async function createPackage(input: CreatePackageInput): Promise<PackageDto> {
  const resp = await apiClient.post<ServiceResponse<PackageDto>>('/packages', input);
  return resp.data.data;
}

/** PATCH /packages/:id — 更新（乐观锁；active 不可改 priceCents / bookCount）。 */
export async function updatePackage(
  id: string,
  patch: UpdatePackageInput,
): Promise<PackageDto> {
  const resp = await apiClient.patch<ServiceResponse<PackageDto>>(
    `/packages/${encodeURIComponent(id)}`,
    patch,
  );
  return resp.data.data;
}

/** POST /packages/:id/publish — 上架（archived → active）。 */
export async function publishPackage(id: string, version: number): Promise<PackageDto> {
  const resp = await apiClient.post<ServiceResponse<PackageDto>>(
    `/packages/${encodeURIComponent(id)}/publish`,
    { version },
  );
  return resp.data.data;
}

/** POST /packages/:id/archive — 下架（active → archived）。 */
export async function archivePackage(id: string, version: number): Promise<PackageDto> {
  const resp = await apiClient.post<ServiceResponse<PackageDto>>(
    `/packages/${encodeURIComponent(id)}/archive`,
    { version },
  );
  return resp.data.data;
}
